import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "locales";
import { getBcp47Locale } from "lib/dateLocale";
import emitter, { EVENTS } from "lib/emitter";
import { stripMetaPrefix } from "lib/messageMetadata";
import providers from "lib/providers";
import "./style.scss";

interface ScheduledMessage {
  id: string;
  threadId: string;
  text: string;
  scheduledAt: string;
  status: string;
}

interface Props {
  threadId: string;
}

function ScheduledMessages({ threadId }: Props) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ScheduledMessage[]>([]);
  const [expanded, setExpanded] = useState(false);
  // Mirror of `messages.length > 0` for the MESSAGES_NEW_MESSAGE handler: a ref
  // (not messages.length in the effect deps) so the listener neither closes
  // over a stale value nor resubscribes on every panel change.
  const hasScheduledRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = await providers.MessagesProvider.scheduledMessages(threadId);
      const list = data || [];
      hasScheduledRef.current = list.length > 0;
      setMessages(list);
    } catch {
      hasScheduledRef.current = false;
      setMessages([]);
    }
  }, [threadId]);

  useEffect(() => {
    load();
    // Reload when a message is scheduled/cancelled in this thread — scheduled
    // rows are filtered out of the normal history, so this panel is the sole
    // surface that reflects them.
    const onChanged = (payload?: { threadId?: string }) => {
      if (!payload || String(payload.threadId) === String(threadId)) load();
    };
    // Also reload when a new message lands in this thread: the scheduler's
    // dispatch arrives exactly like a normal incoming message (the pending row
    // is re-issued under a fresh id and deleted), so this is what clears the
    // "pending" entry the moment it actually sends — without it the panel
    // showed a live Cancel button for an already-delivered message.
    // Empty-panel guard: this panel is mounted in EVERY open chat and the event
    // fires for every incoming message (twice per own send: optimistic + echo).
    // An empty panel has nothing to clear, so skip the backend query — the
    // overwhelmingly common case. SCHEDULED_MESSAGES_CHANGED still populates it
    // on a local schedule.
    const onNewMessage = (msg?: { threadId?: string | number }) => {
      if (!hasScheduledRef.current) return;
      if (!msg || String(msg.threadId) === String(threadId)) load();
    };
    emitter.on(EVENTS.SCHEDULED_MESSAGES_CHANGED, onChanged);
    emitter.on(EVENTS.MESSAGES_NEW_MESSAGE, onNewMessage);
    return () => {
      emitter.off(EVENTS.SCHEDULED_MESSAGES_CHANGED, onChanged);
      emitter.off(EVENTS.MESSAGES_NEW_MESSAGE, onNewMessage);
    };
  }, [load, threadId]);

  const handleCancel = useCallback(async (msgId: string) => {
    try {
      await providers.MessagesProvider.cancelScheduledMessage(msgId);
      setMessages((prev) => {
        const next = prev.filter((m) => m.id !== msgId);
        hasScheduledRef.current = next.length > 0;
        return next;
      });
    } catch (e) {
      console.error("Failed to cancel scheduled message:", e);
    }
  }, []);

  if (messages.length === 0) return null;

  return (
    <div className="scheduled-messages">
      <button type="button" className="scheduled-messages__toggle" onClick={() => setExpanded(!expanded)}>
        <svg viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"
            fill="currentColor"
          />
        </svg>
        {t("Scheduled messages")} ({messages.length})
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 200ms" }}
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="currentColor" />
        </svg>
      </button>
      {expanded && (
        <div className="scheduled-messages__list">
          {messages.map((msg) => (
            <div key={msg.id} className="scheduled-messages__item">
              {/* stripMetaPrefix: a scheduled reply carries the __META__ envelope */}
              <div className="scheduled-messages__item-text">{stripMetaPrefix(msg.text)}</div>
              <div className="scheduled-messages__item-meta">
                <span className="scheduled-messages__item-time">
                  {msg.scheduledAt ? new Date(msg.scheduledAt).toLocaleString(getBcp47Locale()) : ""}
                </span>
                <button type="button" className="scheduled-messages__item-cancel" onClick={() => handleCancel(msg.id)}>
                  {t("Cancel")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ScheduledMessages;
